"use client"

import { DateTime } from "luxon"
import ReactECharts from "echarts-for-react"
import type { EChartsReactProps } from "echarts-for-react"
import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react"
import type { EChartsOption } from "echarts"
import { computeNiceScale } from "@/lib/utils"

const TOP_PADDING = 105
const BOTTOM_PADDING = 24
const LEFT_PADDING = 24
const RIGHT_PADDING = 24
const LEGEND_TOP = 8
const INTERVAL_SERIES_NAME = "Interval flow- 1hr Consumption"
const TOTAL_FLOW_SERIES_NAME = "Total flow- 24hrs Consumption"
const INTERVAL_MASK_AXIS_COLOR = "rgba(31, 119, 180, 0)"
const INTERVAL_MASK_LINE_COLOR = "rgba(28, 238, 242, 0)"

type ChartData = {
  intervalFlowData: [number, number][]
  totalFlowChartData: [number, number, number][]
  maxIntervalFlow: number
  maxTotalFlow: number
}

type WaterConsumptionChartProps = Readonly<{
  chartData: ChartData
  viewMode: "hourly" | "daily" | "weekly"
  selectedDate?: DateTime
  selectedMonth?: DateTime
  selectedWeek?: DateTime
  hideIntervalFlow?: boolean
  unit?: string | null
  totalFlowUnit?: string | null
}>

const localZone = DateTime.local().zone

const NewChart2 = memo(
  forwardRef<ReactECharts, WaterConsumptionChartProps>(function NewChart2(
    {
      chartData,
      viewMode,
      selectedMonth,
      selectedWeek,
      hideIntervalFlow = false,
      unit,
      totalFlowUnit,
    },
    ref,
  ) {
    const { intervalFlowData, totalFlowChartData, maxIntervalFlow, maxTotalFlow } = chartData

    const chartRef = useRef<ReactECharts>(null)
    const [hoverPoint, setHoverPoint] = useState<{
      position: number[]
      visible: boolean
    }>({ position: [0, 0], visible: false })

    useImperativeHandle(ref, () => chartRef.current!)

    const getBarAtTime = useCallback(
      (time: number) => {
        for (const bar of totalFlowChartData) {
          const start = bar[0]
          const end = bar[1]
          if (time >= start && time <= end) {
            return {
              start,
              end,
              value: bar[2],
              center: (start + end) / 2,
            }
          }
        }
        return null
      },
      [totalFlowChartData],
    )

    const getTotalFlowAtTime = useCallback(
      (time: number) => {
        for (const bar of totalFlowChartData) {
          const start = bar[0]
          const end = bar[1]
          if (time >= start && time <= end) {
            return [start, end, bar[2]]
          }
        }
        return [0, 0, 0]
      },
      [totalFlowChartData],
    )

    const tooltipFormatter = useCallback(
      (params: any, hasInterval: boolean, totalFlowName: string) => {
        if (!(params instanceof Array)) return ""
        let time = params[0].axisValue
        let lineVal = null
        let barVal = null

        for (const p of params) {
          if (p.seriesType === "line" && hasInterval) {
            lineVal = p.data![1]
          }
        }

        if (chartRef.current) {
          const chart = chartRef.current.getEchartsInstance()
          const currentOption = (chart.getOption() as any) || {}
          const selected = currentOption?.legend?.[0]?.selected

          const hasTotalFlow = selected?.[totalFlowName] ?? true
          if (hasTotalFlow) {
            const [start, , barValRaw] = getTotalFlowAtTime(time)
            barVal = barValRaw
            if (!hasInterval) time = start
          }
        }

        const dateTypeFormatted = hasInterval ? "HH:mm dd-MMM-yyyy" : "dd-MMM-yyyy"

        const timeStr = DateTime.fromMillis(time, {
          zone: localZone,
        }).toFormat(dateTypeFormatted)

        return `
          <b>Time:</b> ${timeStr}<br/>
          ${!hideIntervalFlow && lineVal !== null ? `<b>Interval Flow:</b> ${lineVal} ${totalFlowUnit || "L"}/hr<br/>` : ""}
          ${barVal !== null ? `<b>Total Flow:</b> ${barVal} ${totalFlowUnit || "L"}/24hrs` : ""}
        `
      },
      [getTotalFlowAtTime, hideIntervalFlow, totalFlowUnit],
    )

    const getTooltipConfig = useCallback(
      (hasInterval: boolean) => ({
        trigger: "axis" as const,
        triggerOn: (hasInterval ? "mousemove|click|mousewheel" : "none") as
          | "mousemove"
          | "click"
          | "mousemove|click|mousewheel"
          | "none",
        position: function (
          point: number[],
          _params: any,
          _dom: any,
          _rect: any,
          size: { contentSize: number[]; viewSize: number[] },
        ): [number, number] {
          const tooltipWidth = size.contentSize[0] ?? 0
          const tooltipHeight = size.contentSize[1] ?? 0
          const chartWidth = size.viewSize[0] ?? 0
          const pointX = point[0] ?? 0
          let x: number

          if (!hasInterval && chartRef.current) {
            const chart = chartRef.current.getEchartsInstance()
            const adjustedPointX = viewMode === "hourly" ? pointX : pointX - LEFT_PADDING
            const converted = chart.convertFromPixel("grid", [adjustedPointX, 0]) as number[]
            const timeAtMouse = Math.ceil(converted[0] ?? 0)
            const barInfo = getBarAtTime(timeAtMouse)

            if (barInfo) {
              const centerPixel = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [
                barInfo.center,
                0,
              ]) as number[]
              x = (centerPixel[0] ?? adjustedPointX) - tooltipWidth / 2
            } else {
              x = adjustedPointX - tooltipWidth / 2
            }
          } else {
            x = pointX - tooltipWidth / 2
          }

          if (x < 0) x = 0
          if (x + tooltipWidth > chartWidth) x = chartWidth - tooltipWidth - 10
          const y = TOP_PADDING - tooltipHeight - 5
          return [x, y]
        },
        axisPointer: {
          label: {
            show: false,
          },
          snap: false,
          type: "line" as const,
          lineStyle: {
            color: "#2bc8db",
            width: 1,
            type: "dashed" as const,
          },
        },
        backgroundColor: "#2bc8db",
        borderRadius: 30,
        textStyle: {
          color: "#ffffff",
          lineHeight: 18,
        },
        borderWidth: 0,
        padding: [6, 16, 6, 16],
        formatter: (params: any) => tooltipFormatter(params, hasInterval, TOTAL_FLOW_SERIES_NAME),
      }),
      [viewMode, getBarAtTime, tooltipFormatter],
    )

    const axisLabelFormatter = useCallback(
      (value: number) => {
        const auTime = DateTime.fromMillis(value, {
          zone: localZone,
        })

        const startOfNextWeek = selectedWeek?.plus({ weeks: 1 }).startOf("week").toMillis()

        if (value === startOfNextWeek) return ""

        const startOfNextMonth = selectedMonth?.plus({ month: 1 }).startOf("month").toMillis()

        if (value === startOfNextMonth) return ""

        let format: string

        switch (viewMode) {
          case "hourly":
            format = "HH:mm"
            break
          case "daily":
            format = "dd-MMMM"
            break
          case "weekly":
            format = "EEEE"
            break
          default:
            format = "dd-MMMM"
        }

        return auTime.toFormat(format)
      },
      [viewMode, selectedMonth, selectedWeek],
    )

    const TARGET_TICKS = 5

    const leftScale = computeNiceScale(0, maxTotalFlow, TARGET_TICKS)
    const minTotalFlow = leftScale.min
    const maxTotalFlowNice = leftScale.max
    const splitNumber = leftScale.tickCount

    const rightScale = computeNiceScale(0, maxIntervalFlow, TARGET_TICKS, leftScale.tickCount)
    const maxIntervalFlowNice = rightScale.max

    const renderItem = useCallback(
      function (_params: any, api: any) {
        const startTime = api.value(0)
        const endTime = api.value(1)
        const val = api.value(2)
        const coordStart = api.coord([startTime, val])
        const coordEnd = api.coord([endTime, val])

        return {
          type: "rect" as const,
          shape: {
            x: coordStart[0],
            y: coordStart[1],
            width: coordEnd[0] - coordStart[0],
            height: api.size([0, val - minTotalFlow])[1],
          },
          style: {
            fill: "#ada212",
          },
        }
      },
      [minTotalFlow],
    )

    const option = useMemo<EChartsOption>(
      () => ({
        grid: {
          top: TOP_PADDING,
          bottom: BOTTOM_PADDING,
          left: LEFT_PADDING,
          right: RIGHT_PADDING,
          containLabel: true,
        },
        animation: true,
        animationDuration: 1000,
        animationEasing: "cubicOut",
        legend: {
          top: LEGEND_TOP,
          textStyle: {
            color: "#aec7e8",
          },
          formatter: (name: string) =>
            hideIntervalFlow && name === INTERVAL_SERIES_NAME ? " " : name,
        },
        backgroundColor: "#222629",
        graphic: {
          elements: [
            {
              type: "circle" as const,
              id: "move-point",
              shape: { r: 5 },
              z: 1000,
              style: {
                fill: "#1ceef2",
              },
              invisible: !hoverPoint.visible,
              position: hoverPoint.position,
            },
          ],
        },
        tooltip: getTooltipConfig(true),
        xAxis: {
          type: "time" as const,
          splitNumber,
          axisLabel: {
            formatter: axisLabelFormatter,
          },
        },
        yAxis: [
          {
            type: "value" as const,
            axisLabel: {
              formatter: (v: number) => `${v} ${totalFlowUnit || "L"}`,
              color: "#aec7e8",
            },
            min: minTotalFlow,
            splitLine: {
              show: false,
            },
            name: `Total Flow (${totalFlowUnit || "L"})`,
            max: maxTotalFlowNice,
            interval: leftScale.step,
            splitNumber,
            axisLine: {
              lineStyle: {
                color: "#aec7e8",
              },
            },
            nameTextStyle: {
              color: "#aec7e8",
              fontSize: 14,
              fontWeight: "bold",
            },
          },
          {
            type: "value" as const,
            axisLabel: {
              formatter: (v: number) => (hideIntervalFlow ? "" : `${v} ${unit || "L"}`),
              color: hideIntervalFlow ? INTERVAL_MASK_AXIS_COLOR : "#1f77b4",
            },
            min: 0,
            name: hideIntervalFlow ? "" : `Interval Flow (${unit || "L"})`,
            max: maxIntervalFlowNice,
            interval: rightScale.step,
            axisLine: {
              lineStyle: {
                color: hideIntervalFlow ? INTERVAL_MASK_AXIS_COLOR : "#1f77b4",
              },
            },
            nameTextStyle: {
              color: hideIntervalFlow ? INTERVAL_MASK_AXIS_COLOR : "#1f77b4",
              fontSize: 14,
              fontWeight: "bold",
            },
          },
        ],
        series: [
          {
            id: "total-flow-series",
            type: "custom" as const,
            name: TOTAL_FLOW_SERIES_NAME,
            yAxisIndex: 0,
            itemStyle: {
              color: "#ada212",
            },
            renderItem,
            encode: {
              x: [0, 1],
              y: 2,
            },
            data: totalFlowChartData,
            z: 2,
          },
          {
            id: "interval-flow-series",
            type: "line" as const,
            name: INTERVAL_SERIES_NAME,
            yAxisIndex: 1,
            smooth: true,
            showAllSymbol: false,
            emphasis: {
              focus: "none" as const,
            },
            symbol: "none" as const,
            data: intervalFlowData,
            lineStyle: {
              color: hideIntervalFlow ? INTERVAL_MASK_LINE_COLOR : "#1ceef2",
              opacity: hideIntervalFlow ? 0 : 1,
            },
            itemStyle: {
              color: hideIntervalFlow ? INTERVAL_MASK_LINE_COLOR : "#1ceef2",
              opacity: hideIntervalFlow ? 0 : 1,
            },
            z: 3,
          },
        ],
      }),
      [
        intervalFlowData,
        totalFlowChartData,
        getTooltipConfig,
        axisLabelFormatter,
        hoverPoint,
        minTotalFlow,
        maxTotalFlowNice,
        maxIntervalFlowNice,
        leftScale.step,
        rightScale.step,
        splitNumber,
        renderItem,
        hideIntervalFlow,
        totalFlowUnit,
        unit,
      ],
    )

    const onEvents: EChartsReactProps["onEvents"] = useMemo(
      () => ({
        legendselectchanged: (params: any) => {
          if (!chartRef.current) return

          const chart = chartRef.current.getEchartsInstance()
          const selected = params.selected
          const hasInterval = hideIntervalFlow ? false : (selected[INTERVAL_SERIES_NAME] ?? true)
          const hasTotal = selected[TOTAL_FLOW_SERIES_NAME] ?? true

          chart.setOption(
            {
              tooltip: getTooltipConfig(hasInterval),
              yAxis: [
                {
                  name: hasTotal ? `Total Flow (${totalFlowUnit || "L"})` : "",
                },
                {
                  name: hasInterval ? `Interval Flow (${unit || "L"})` : "",
                },
              ],
            },
            { replaceMerge: ["tooltip"] as any },
          )
        },
        updateAxisPointer: (params: any) => {
          if (!chartRef.current) return

          const chart = chartRef.current.getEchartsInstance()
          const pointInfo = params.axesInfo?.[0]
          if (!pointInfo || pointInfo.value == null) return

          const snappedX = pointInfo.value
          const currentOption = chart.getOption() || {}
          const selected = (currentOption as any)?.legend?.[0]?.selected ?? {}

          const hasInterval = hideIntervalFlow ? false : (selected[INTERVAL_SERIES_NAME] ?? true)
          if (!hasInterval) return

          const seriesData = (currentOption as any)?.series?.[1]?.data ?? []
          const idx = seriesData.findIndex((item: any) => item[0] === snappedX)
          if (idx !== -1) {
            const snappedY = seriesData[idx][1]
            const pixel = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 1 }, [
              snappedX,
              snappedY,
            ])

            setHoverPoint({ position: pixel, visible: true })
          }
        },
      }),
      [getTooltipConfig, hideIntervalFlow, totalFlowUnit, unit],
    )

    const onMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!chartRef.current) return

        const chart = chartRef.current.getEchartsInstance()
        const dom = chart.getDom()
        const currentOption = chart.getOption() || {}
        const selected = (currentOption as any)?.legend?.[0]?.selected ?? {}

        const hasInterval = hideIntervalFlow ? false : (selected[INTERVAL_SERIES_NAME] ?? true)
        const hasTotal = selected[TOTAL_FLOW_SERIES_NAME] ?? true

        if (hasInterval || !hasTotal) return

        const rect = dom.getBoundingClientRect()
        const offsetX = e.clientX - rect.left

        const timeArr = chart.convertFromPixel("grid", [offsetX, 0])
        if (!Array.isArray(timeArr) || timeArr[0] == null || isNaN(timeArr[0])) return
        const time = timeArr[0]

        const barInfo = getBarAtTime(time)
        if (!barInfo) return

        const pixel = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [
          barInfo.center,
          barInfo.value,
        ])

        if (pixel && pixel[0] != null && pixel[1] != null && !isNaN(pixel[0]) && !isNaN(pixel[1])) {
          setHoverPoint({ position: pixel, visible: true })

          chart.setOption({
            xAxis: {
              axisPointer: {
                value: barInfo.center,
                status: "show",
              },
            },
          })
        }
      },
      [getBarAtTime, hideIntervalFlow],
    )

    const onMouseLeave = useCallback(() => {
      setHoverPoint((prev) => ({ ...prev, visible: false }))
      if (chartRef.current) {
        chartRef.current.getEchartsInstance().setOption({
          xAxis: { axisPointer: { status: "hide" } },
        })
      }
    }, [])

    return (
      <div onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        <ReactECharts
          ref={chartRef}
          option={option}
          onEvents={onEvents}
          notMerge={false}
          lazyUpdate={false}
          className="min-h-[26vw] overflow-hidden rounded-sm"
        />
      </div>
    )
  }),
)

NewChart2.displayName = "NewChart2"
export default NewChart2
